import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { PROFILE, HERO_LINES, IMAGES } from "@/data/content";

const COLORS = ["text-yellow", "text-white", "text-coral"];

const wrap = { hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.2 } } };
const child = {
  hidden: { y: "115%", rotate: 4 },
  show: { y: "0%", rotate: 0, transition: { type: "spring", stiffness: 90, damping: 14 } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const faceY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative min-h-[100svh] w-full overflow-hidden flex items-center pt-24 pb-16">
      {/* playful color blobs */}
      <motion.div style={{ y: faceY }} className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-white/30 blur-3xl" />
        <div className="absolute top-1/3 -right-16 h-80 w-80 rounded-full bg-coral/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-yellow/25 blur-3xl" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 w-full px-5 md:px-10 max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-obsidian rounded-full px-4 py-2 mb-7"
        >
          <Sparkles size={16} className="text-yellow" />
          <span className="font-outfit text-xs md:text-sm uppercase tracking-widest text-white">
            {PROFILE.role}
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-6 items-center">
          <div>
            <motion.h1
              variants={wrap}
              initial="hidden"
              animate="show"
              className="font-anton uppercase leading-[0.82] tracking-tight text-[19vw] md:text-[14vw] lg:text-[11vw]"
            >
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden py-1">
                  <motion.span variants={child} className={`block ${COLORS[i % COLORS.length]}`}>
                    {line}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.6 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <motion.button
                data-testid="hero-cta-partner"
                onClick={() => scrollToId("contact")}
                whileHover={{ scale: 1.06, rotate: -2 }}
                whileTap={{ scale: 0.95 }}
                className="font-outfit font-extrabold text-sm uppercase tracking-wide bg-obsidian text-white px-8 py-4 rounded-full hover:bg-coral transition-colors duration-200"
              >
                Let&apos;s Partner Up
              </motion.button>
              <motion.button
                data-testid="hero-cta-work"
                onClick={() => scrollToId("videos")}
                whileHover={{ scale: 1.06, rotate: 2 }}
                whileTap={{ scale: 0.95 }}
                className="font-outfit font-extrabold text-sm uppercase tracking-wide text-yellow border-2 border-yellow px-8 py-4 rounded-full hover:bg-yellow hover:text-obsidian transition-colors duration-200"
              >
                Watch The Chaos
              </motion.button>
            </motion.div>
          </div>

          {/* Floating headshot + speech bubble */}
          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 90, damping: 13 }}
            className="flex justify-center items-end relative mt-10 lg:mt-0"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
              className="relative"
            >
              <div className="absolute inset-x-0 bottom-4 mx-auto h-72 w-72 rounded-full bg-yellow/50 blur-3xl -z-10" />
              <img
                src={IMAGES.heroCutout}
                alt="Justin Leusner"
                className="h-64 sm:h-80 lg:h-[36rem] xl:h-[42rem] w-auto object-contain drop-shadow-2xl"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.6, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 1, type: "spring", stiffness: 200, damping: 12 }}
                className="absolute -top-8 sm:-top-6 lg:top-2 -left-3 sm:-left-6 lg:-left-8 xl:-left-14"
              >
                <motion.div
                  animate={{ rotate: [-4, 3, -4] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  className="relative bg-white text-obsidian font-anton uppercase text-xs sm:text-base xl:text-xl px-3 sm:px-5 py-2 sm:py-3 rounded-2xl shadow-xl whitespace-nowrap"
                  data-testid="hero-bubble"
                >
                  Hey! it&apos;s Justin! 🥳
                  <span className="absolute -bottom-1.5 left-10 h-4 w-4 bg-white rotate-45 rounded-[3px]" />
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        style={{ opacity: fade }}
        onClick={() => scrollToId("marquee")}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white bg-obsidian rounded-full p-3"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.4 }} className="block">
          <ArrowDown size={22} className="text-white" />
        </motion.span>
      </motion.button>
    </section>
  );
};
